import React, { useEffect, useRef, useState } from 'react';

import GridLayout, { type Layout, type LayoutItem, getCompactor, useContainerWidth } from 'react-grid-layout';

import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import type { LensCard, LensCardLayout } from '~/types/leLens';

import {
  CANVAS_COLUMNS,
  canvasColumnsForWidth,
  frameForCard,
  minimumCanvasHeight,
  minimumCanvasWidth,
  projectCanvasFrames,
  restoreCanvasFrames,
} from '../canvasLayout';

import { keyboardCanvasLayout } from './canvasKeyboard';
import { LENS_DRAG_HANDLE_CLASS, LENS_FRAME_GAP_PX, LENS_FRAME_ROW_PX } from './cardGrid';
import useCanvasAutoScroll from './useCanvasAutoScroll';

interface LensCanvasProps {
  cards: Array<LensCard>;
  canEdit: boolean;
  /** True while a save is in flight or the board is in bulk-selection mode. */
  isLocked: boolean;
  onLayoutChange: (frames: Record<string, LensCardLayout>, message: string) => void;
  renderCard: (
    card: LensCard,
    state: { isDragging: boolean; canDrag: boolean; freeColumnsRight: number; frame: LensCardLayout },
  ) => React.ReactNode;
}

/** Use the library's standard vertical packing: make room without overlapping cards. */
export const LENS_CANVAS_COMPACTOR = getCompactor('vertical');
export const LENS_CANVAS_STYLES: SxProps<Theme> = {
  position: 'relative',
  // The library's stylesheet, ported rather than imported: it is a short
  // sheet, the repo styles through MUI, and jest has no loader for a
  // vendor stylesheet. Ported in full from its source, because guessing
  // at which rules matter is how the container lost `position: relative`
  // and every card anchored itself to the document instead of the board.
  '& .react-grid-layout': { position: 'relative' },
  '& .react-grid-item': {
    // A canvas-width measurement can arrive immediately after the pane opens,
    // a filter refreshes, or a browser restores a tab. Animating that measured
    // correction makes cards visibly shrink to an old frame, then slide back
    // to their real one. Layout changes should be direct; the live drag and
    // resize states below still give the deliberate gesture its feedback.
    transition: 'none',
  },
  '& .react-grid-item.cssTransforms': { transition: 'none' },
  '& .react-grid-item.resizing': {
    // The lift used to be keyed to the card's own resize handle, which the
    // engine replaced, so a resized card had stopped rising off the board.
    boxShadow: 3,
    transition: 'none',
    willChange: 'width, height',
    zIndex: 1,
  },
  '& .react-grid-item.react-draggable-dragging': {
    transition: 'none',
    willChange: 'transform',
    zIndex: 3,
  },
  '& .react-grid-item.dropping': { visibility: 'hidden' },
  '& .react-grid-item.react-grid-placeholder': {
    backgroundColor: 'primary.main',
    borderRadius: '2px',
    opacity: 0.16,
    transitionDuration: '100ms',
    userSelect: 'none',
    zIndex: 2,
  },
  '& .react-grid-item.react-grid-placeholder.placeholder-resizing': { transition: 'none' },
  // The one real resize affordance. It is supplied to react-grid-layout as
  // its actual `handleComponent`, rather than being a decorative card button
  // beside the library's invisible default corner. Keeping it visible in edit
  // mode makes the direct-manipulation path discoverable without stealing
  // header width from the title or metric.
  '& .lens-card-resize-handle': {
    position: 'absolute',
    touchAction: 'none',
    zIndex: 4,
    borderRadius: '2px',
    '&:hover, &:focus-visible': { backgroundColor: 'action.hover', outline: '2px solid', outlineColor: 'primary.main' },
  },
  '& .lens-card-resize-se': {
    width: 24,
    height: 24,
    right: 2,
    bottom: 2,
    cursor: 'se-resize',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 5,
      bottom: 5,
      width: 7,
      height: 7,
      borderRight: '2px solid',
      borderBottom: '2px solid',
      borderColor: 'text.secondary',
    },
  },
  '& .lens-card-resize-e': {
    width: 10,
    height: 'calc(100% - 64px)',
    right: -4,
    top: 32,
    cursor: 'ew-resize',
  },
  '& .lens-card-resize-s': {
    height: 10,
    width: 'calc(100% - 64px)',
    bottom: -4,
    left: 32,
    cursor: 'ns-resize',
  },
  '& .react-resizable-hide > .react-resizable-handle': { display: 'none' },
};

function Canvas({ cards, canEdit, isLocked, onLayoutChange, renderCard }: LensCanvasProps) {
  // v2 measures through a hook rather than the old width-provider wrapper.
  // Do not mount a desktop-shaped grid and then "correct" it after the
  // observer fires: that is the visible revert people see when a pane opens or
  // a Lens refreshes. The empty wrapper can be measured, then the grid mounts
  // once with the actual canvas width.
  const { width, containerRef, mounted } = useContainerWidth({ measureBeforeMount: true, initialWidth: 0 });
  // Only so the grip's tooltip can stand down while the card is in flight: held
  // open it covers the placeholder the drag exists to follow.
  const [draggingId, setDraggingId] = useState<string | null>(null);
  // Let the grid own the live gesture. Retain only the finished layout while
  // its save is pending; feeding intermediate frames back fights compaction.
  const [gestureLayout, setGestureLayout] = useState<Layout | null>(null);
  const wasLocked = useRef(isLocked);
  const [resizing, setResizing] = useState(false);
  useCanvasAutoScroll(Boolean(draggingId) || resizing, containerRef);

  // A gesture that ends where it started still reports a layout. Saving it
  // spends a write, a refetch and a snackbar on nothing, and turns any
  // transient network failure into an error the reader did not cause.
  const save = (next: Layout, message: string) => {
    const visualFrames = framesOf(next);
    const frames = visualColumns === CANVAS_COLUMNS ? visualFrames : restoreCanvasFrames(visualFrames, visualColumns);
    if (framesChanged(cards, frames)) {
      onLayoutChange(frames, message);
      return true;
    }
    return false;
  };
  // The board keeps its authored twelve-column geometry throughout desktop
  // resizing. Only a real phone gets a read-only single-column projection.
  const visualColumns = canvasColumnsForWidth(width);
  const editable = mounted && canEdit && !isLocked && visualColumns > 1;

  const sourceLayout = projectFramesForCanvasPreview(cards, {}, visualColumns);
  const layout = gestureLayout ?? sourceLayout;

  useEffect(() => {
    // A successful save has promoted the local draft to the card source; a
    // failed save has restored it. Either way the next unlocked frame is the
    // authoritative one and the temporary gesture must not linger.
    if (wasLocked.current && !isLocked) {
      setGestureLayout(null);
    }
    wasLocked.current = isLocked;
  }, [isLocked]);

  return (
    // The grid constrains the cards themselves. Do not clip this wrapper: a
    // selected card's deliberate outline sits two pixels outside its frame,
    // and clipping it makes the selection look broken at the board edge.
    <Box ref={containerRef} sx={{ ...LENS_CANVAS_STYLES, minWidth: 0 }}>
      {mounted && (
        <GridLayout
          // v2 groups its options rather than taking two dozen flat props.
          compactor={LENS_CANVAS_COMPACTOR}
          dragConfig={{
            enabled: editable,
            handle: `.${LENS_DRAG_HANDLE_CLASS}`,
          }}
          gridConfig={{
            cols: visualColumns,
            containerPadding: [0, 0],
            margin: [LENS_FRAME_GAP_PX, LENS_FRAME_GAP_PX],
            rowHeight: LENS_FRAME_ROW_PX,
          }}
          layout={layout}
          onDragStart={(_layout, item) => {
            setDraggingId(item.i);
          }}
          onDragStop={(next) => {
            setDraggingId(null);
            setGestureLayout(next);
            if (!save(next, 'Card placed')) setGestureLayout(null);
          }}
          onResizeStart={() => setResizing(true)}
          onResizeStop={(next) => {
            setResizing(false);
            setGestureLayout(next);
            if (!save(next, 'Card resized')) setGestureLayout(null);
          }}
          resizeConfig={{
            enabled: editable,
            handles: ['e', 's', 'se'],
            handleComponent: (axis, ref) => (
              <span
                role="button"
                tabIndex={axis === 'se' ? 0 : -1}
                aria-label={axis === 'e' ? 'Resize card width' : axis === 's' ? 'Resize card height' : 'Resize card'}
                className={`lens-card-resize-handle lens-card-resize-${axis} react-resizable-handle`}
                ref={ref}
                title={
                  axis === 'e'
                    ? 'Drag to change width'
                    : axis === 's'
                      ? 'Drag to change height'
                      : 'Drag or use arrow keys to resize card'
                }
              />
            ),
          }}
          width={width}
        >
          {cards.map((card) => {
            const item = layout.find((entry) => entry.i === card.id);
            const freeColumnsRight = item ? contiguousFreeColumnsRight(item, layout, visualColumns) : 0;
            // `item` is the frame actually painted by react-grid-layout. A
            // newly generated card may not have persisted `card.layout` yet,
            // and a narrow canvas may be showing a projected frame. Passing
            // the painted geometry keeps the card's own boundary aligned with
            // the resize handle instead of letting its content grow below it.
            const frame = item ? { x: item.x, y: item.y, w: item.w, h: item.h } : (card.layout ?? frameForCard(card));
            return (
              <Box
                data-lens-card-frame={card.id}
                key={card.id}
                sx={{ height: '100%', minHeight: 0, minWidth: 0 }}
                onKeyDown={(event) => {
                  if (!editable || !event.key.startsWith('Arrow')) return;
                  const target = event.target as HTMLElement;
                  const resizingWithKeys = Boolean(target.closest('.lens-card-resize-handle'));
                  if (!resizingWithKeys && !target.closest(`.${LENS_DRAG_HANDLE_CLASS}`)) return;
                  event.preventDefault();
                  event.stopPropagation();
                  const next = keyboardCanvasLayout(
                    layout,
                    card.id,
                    event.key,
                    resizingWithKeys ? 'resize' : 'move',
                    visualColumns,
                  );
                  setGestureLayout(next);
                  if (!save(next, resizingWithKeys ? 'Card resized' : 'Card placed')) setGestureLayout(null);
                }}
              >
                <Box data-lens-card-content sx={{ height: '100%', minHeight: 0, minWidth: 0, width: '100%' }}>
                  {renderCard(card, {
                    isDragging: draggingId === card.id,
                    canDrag: editable,
                    freeColumnsRight,
                    frame,
                  })}
                </Box>
              </Box>
            );
          })}
        </GridLayout>
      )}
    </Box>
  );
}

/**
 * Empty canvas columns directly beside a card for its full height. A reading
 * sidecar may borrow this space in view mode without covering or moving any
 * saved card; a card beginning farther right is the hard stop.
 */
export function contiguousFreeColumnsRight(item: LayoutItem, layout: Layout, columns: number): number {
  const right = item.x + item.w;
  let stop = columns;
  for (const other of layout) {
    if (other.i === item.i || other.x < right) continue;
    const overlapsVertically = other.y < item.y + item.h && other.y + other.h > item.y;
    if (overlapsVertically) {
      stop = Math.min(stop, other.x);
    }
  }
  return Math.max(0, stop - right);
}

/**
 * Whether the engine actually moved anything.
 *
 * A gesture that ends where it started still reports a layout, and saving that
 * spends a write, a refetch and a snackbar on nothing.
 */
export function framesChanged(cards: ReadonlyArray<LensCard>, frames: Record<string, LensCardLayout>): boolean {
  return cards.some((card) => {
    const frame = frames[card.id];
    const current = card.layout ?? frameForCard(card);
    if (!frame || !current) {
      return false;
    }
    return frame.x !== current.x || frame.y !== current.y || frame.w !== current.w || frame.h !== current.h;
  });
}

/** Render source frames without ever mutating the saved Lens geometry. */
export function projectFramesForCanvasPreview(
  cards: ReadonlyArray<LensCard>,
  frames: Readonly<Record<string, LensCardLayout>>,
  visualColumns: number,
): Array<LayoutItem> {
  const projected = projectCanvasFrames(
    cards,
    (card) => frames[card.id] ?? card.layout ?? frameForCard(card),
    visualColumns,
  );
  return cards.map((card) => {
    const frame = projected[card.id] ?? frames[card.id] ?? card.layout ?? frameForCard(card);
    return {
      i: card.id,
      ...frame,
      minH: card.type === 'table' ? Math.min(frame.h, minimumCanvasHeight(card)) : minimumCanvasHeight(card),
      ...(card.type === 'table' && frame.h < minimumCanvasHeight(card) ? { isResizable: false } : {}),
      // Projected layouts never save, but the engine still validates them.
      // Their visual one-column floor therefore has to be 1, not the desktop
      // resize floor of 2.
      minW: visualColumns === CANVAS_COLUMNS ? minimumCanvasWidth(card) : 1,
    };
  });
}

/** The engine reports every card, since compaction moves more than the one dragged. */
function framesOf(layout: Layout): Record<string, LensCardLayout> {
  return Object.fromEntries(layout.map((item) => [item.i, { x: item.x, y: item.y, w: item.w, h: item.h }]));
}

/**
 * A narrow board renders a safe projection of the saved coordinates. Choose
 * the result under the pointer in that projection, then exchange the two
 * source frames so the saved desktop composition keeps its original sizes.
 */
export default Canvas;
